import type { PortfolioProjectKey } from '@/lib/portfolioProjects';
import type { Locale } from '@/lib/serviceCatalog';

type L = { tr: string; en: string };

export type TestimonialEntry = {
  id: string;
  project: PortfolioProjectKey;
  brand: string;
  initials: string;
  role: L;
  market: L;
  /** Country of the client company, only where the case study or quote states it. */
  clientCountry?: L;
  deliverable: L;
  tags: Array<{ text: L; featured?: boolean }>;
  quote: L;
};

/**
 * Quotes are attributed to real client brands: every claim must match the case study, and each
 * text needs the client's written approval before it ships. No Review/AggregateRating schema is
 * emitted for these on purpose (self-serving reviews are ineligible for rich results).
 */
export const TESTIMONIALS: TestimonialEntry[] = [
  {
    id: 'backdoor-tickets',
    project: 'p10',
    brand: 'Backdoor Tickets',
    initials: 'BT',
    role: { tr: 'Etkinlik biletleme SaaS · Almanya', en: 'Event ticketing SaaS · Germany' },
    market: { tr: 'Almanya', en: 'Germany' },
    clientCountry: { tr: 'Almanya', en: 'Germany' },
    deliverable: { tr: 'SaaS ürün arayüzü', en: 'SaaS product interface' },
    tags: [
      { text: { tr: 'Avrupa pazarı', en: 'European market' }, featured: true },
      { text: { tr: 'UI/UX', en: 'UI/UX' } },
    ],
    quote: {
      tr: 'Almanya’daki mekanlar için bir biletleme ürünü geliştiriyoruz ve bu pazarda veri egemenliği pazarlık konusu bile değil. Figtures ekibi işe ekranlardan önce operasyonu anlamakla başladı: promoter’lar satışı nasıl takip ediyor, kapıda en yoğun anda görevli ne görmek istiyor. Tasarım ve geliştirme aynı ekipte olduğu için yorumlarımız ürüne günler değil saatler içinde yansıdı. İstanbul’daki ekiple tamamen uzaktan çalıştık; ne zaman farkı ne de dil konusunda bir aksama yaşadık.',
      en: 'We build ticketing for venues in Germany, and in this market data sovereignty is not up for debate. The Figtures team started with our operations rather than our screens: how promoters track sales, and what door staff need to see at the busiest moment of the night. Because design and engineering sit in one team, our feedback reached the product in hours, not days. We worked fully remotely with their Istanbul team and never had an issue with time zones or language.',
    },
  },
  {
    id: '3d-yaninda',
    project: 'p1',
    brand: '3D Yanında',
    initials: '3D',
    role: { tr: 'Dijital üretim ve endüstriyel 3D · Türkiye', en: 'Digital production and industrial 3D · Turkey' },
    market: { tr: 'Türkiye', en: 'Turkey' },
    clientCountry: { tr: 'Türkiye', en: 'Turkey' },
    deliverable: { tr: 'Kurumsal web sitesi', en: 'Corporate website' },
    tags: [
      { text: { tr: 'Canlıda', en: 'Live' }, featured: true },
      { text: { tr: 'Endüstriyel üretim', en: 'Manufacturing' } },
    ],
    quote: {
      tr: '3D tarama, CAD modelleme ve hassas baskıyı sanayiciye anlatmak sandığımızdan zordu. Figtures bize önce şu soruyu sordu: fabrikada kırılan bir parçanın yenisini bulamayan bakım sorumlusu internette ne arar, neye güvenir? Site bu sorunun cevabı üzerine kuruldu. Tersine mühendislikten baskıya kadar süreci adım adım anlatıyor ve teklif talebini tek bir akışta topluyor. Her hafta kısa bir durum görüşmesi yaptık, hiçbir kararı tahminle vermedik.',
      en: 'Explaining 3D scanning, CAD modelling and precision printing to manufacturers turned out to be harder than we expected. Figtures began with one question: when a part breaks on the factory floor and no replacement exists, what does the maintenance lead search for online, and what makes them trust a supplier? The site is built around that answer. It walks through the process from reverse engineering to print and gathers quote requests into a single flow. We had a short status call every week, and no decision was left to guesswork.',
    },
  },
  {
    id: 'ztech',
    project: 'p4',
    brand: 'Ztech',
    initials: 'ZT',
    role: { tr: 'Çocuklar için teknoloji ve yapay zeka eğitimi', en: 'Technology and AI education for kids' },
    market: { tr: 'Türkiye', en: 'Turkey' },
    clientCountry: { tr: 'Türkiye', en: 'Turkey' },
    deliverable: { tr: 'Ana sayfa ve kampanya yapısı', en: 'Homepage and campaign system' },
    tags: [
      { text: { tr: 'Canlıda', en: 'Live' }, featured: true },
      { text: { tr: 'EdTech', en: 'EdTech' } },
    ],
    quote: {
      tr: 'Sitemizde aynı anda iki kişiye konuşmamız gerekiyor: kodlamayı ve yapay zekayı merak eden çocuğa, bir de kararı veren veliye. Figtures ana sayfayı bu iki bakış açısını dengeleyecek şekilde kurguladı. Yapay zeka eğitimini korkutucu değil, anlaşılır ve eğlenceli gösteren bir dil bulduk. Kampanya dönemleri için ayrı bir sayfa düzeni kurguladık; dönem başındaki kayıt yoğunluğunda veliye doğru mesajı doğru anda gösterebiliyoruz.',
      en: 'Our site has to speak to two people at once: the child who is curious about coding and AI, and the parent who makes the decision. Figtures designed the homepage to balance both. Together we found a way to present AI education as approachable and fun rather than intimidating. We also designed a dedicated campaign layout, so when enrolment peaks at the start of each term, parents see the right message at the right moment.',
    },
  },
  {
    id: 'kolektif',
    project: 'p6',
    brand: 'Kolektif Fuarcılık',
    initials: 'KF',
    role: { tr: 'Uluslararası fuar partneri', en: 'International exhibition partner' },
    market: { tr: 'Türkiye ve uluslararası', en: 'Turkey and international' },
    deliverable: { tr: 'Kurumsal portföy sitesi', en: 'Corporate portfolio site' },
    tags: [
      { text: { tr: 'Canlıda', en: 'Live' }, featured: true },
      { text: { tr: 'B2B', en: 'B2B' } },
    ],
    quote: {
      tr: 'Fuarcılıkta ilk izlenim, stant kurulmadan çok önce web sitesinde oluşuyor. Yurt dışından gelen katılımcılara uluslararası ölçekte çalışan, güvenilir bir partner olduğumuzu anlatmamız gerekiyordu. Figtures portföyümüzü fuar ve sektör bazında yeniden düzenledi, kurumsal dilimizi sadeleştirdi ve ziyaretçinin aradığı fuara birkaç tıkla ulaşmasını sağladı. Artık bir firmayla ilk görüşmeye oturduğumuzda karşı taraf bizi çoğu zaman siteden tanıyarak geliyor.',
      en: 'In the exhibition business, the first impression is formed on the website long before the stand goes up. We needed to show international exhibitors that we are a reliable partner operating at international scale. Figtures reorganised our portfolio by fair and industry, simplified our corporate voice, and made it possible to reach any fair in a few clicks. When we sit down with a new company today, they usually arrive already knowing us from the site.',
    },
  },
  {
    id: 'evarkadasi',
    project: 'p8',
    brand: 'evarkadaşı',
    initials: 'EA',
    role: { tr: 'Oda ve ev arkadaşı ilan platformu · Türkiye', en: 'Room and flatmate listings · Turkey' },
    market: { tr: 'Türkiye', en: 'Turkey' },
    clientCountry: { tr: 'Türkiye', en: 'Turkey' },
    deliverable: { tr: 'Arama odaklı ana sayfa', en: 'Search-first homepage' },
    tags: [
      { text: { tr: 'Canlıda', en: 'Live' }, featured: true },
      { text: { tr: 'Marketplace', en: 'Marketplace' } },
    ],
    quote: {
      tr: 'Siteye gelen herkesin ilk sorusu aynı: benim şehrimde, bütçeme uygun bir oda var mı? Figtures ana sayfayı tamamen bu soruya cevap verecek şekilde, aramayı merkeze alarak yeniden kurdu. Şehir seçip ilanları keşfetmek birkaç dokunuşa indi; bunun mobilde de aynı rahatlıkla çalışması bizim için kritikti. Süreç boyunca her tasarım kararının nedenini açıkça anlattılar; bu, ekip içinde fikir birliğini çok kolaylaştırdı.',
      en: 'Everyone who lands on our site has the same first question: is there a room in my city that fits my budget? Figtures rebuilt the homepage around answering exactly that, with search at the centre. Choosing a city and browsing listings now takes a few taps, and it was critical for us that this felt just as easy on mobile. They explained the reasoning behind each design decision throughout, which made it much easier to reach agreement inside our own team.',
    },
  },
  {
    id: 'outfica',
    project: 'p3',
    brand: 'Outfica',
    initials: 'OF',
    role: { tr: 'Yapay zeka destekli moda SaaS', en: 'AI-powered fashion SaaS' },
    market: { tr: 'Global', en: 'Global' },
    deliverable: { tr: 'SaaS paneli ve katalog deneyimi', en: 'SaaS dashboard and catalogue' },
    tags: [
      { text: { tr: 'AI ürün', en: 'AI product' }, featured: true },
      { text: { tr: 'SaaS', en: 'SaaS' } },
    ],
    quote: {
      tr: 'Yapay zeka destekli moda araçlarımızı, teknik olmayan bir kullanıcının bile rahatça kullanabileceği sade bir panele sığdırmak istiyorduk. Figtures, model çıktılarının kullanıcıya nasıl gösterileceğinden karanlık tema detaylarına kadar ürünü bizimle birlikte düşündü. Bir AI özelliğini demo olmaktan çıkarıp gerçek kullanıcıya açmanın ne demek olduğunu bilen bir ekiple çalışmak büyük fark yarattı. Katalog deneyimi artık hem markalara hem son kullanıcıya anlaşılır geliyor.',
      en: 'We wanted our AI-powered fashion tools to fit into a clean dashboard that even a non-technical user could navigate with ease. Figtures thought the product through with us, from how model outputs are presented to the details of the dark theme. Working with a team that knows what it takes to turn an AI feature from a demo into something real users rely on made a big difference. The catalogue experience now makes sense to brands and end users alike.',
    },
  },
];

export const TESTIMONIALS_COPY = {
  intro: {
    tr: 'Türkiye’den Almanya’ya, farklı sektörlerde ürün teslim ettiğimiz ekiplerin kendi sözleriyle Figtures ile çalışmak.',
    en: 'What it is like to work with Figtures, in the words of teams we have shipped for across industries, from Turkey to Germany.',
  },
  caseStudy: { tr: 'Vaka çalışması', en: 'Case study' },
  previous: { tr: 'Önceki yorum', en: 'Previous testimonial' },
  next: { tr: 'Sonraki yorum', en: 'Next testimonial' },
  goTo: { tr: (i: number) => `${i + 1}. yoruma git`, en: (i: number) => `Go to testimonial ${i + 1}` },
} satisfies Record<string, { tr: unknown; en: unknown }>;

export function localizeTestimonial(item: L, lang: Locale): string {
  return item[lang];
}
